import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { adminService, logoUploadService, GeneralInfo } from '../../services/admin.service';
import { getImageUrl } from '../../services/api';
import { Save, Building2, Upload, X, Phone, Mail, MapPin, CreditCard } from 'lucide-react';

// Helper function to construct full URL from logo filename
const constructLogoUrl = (filename: string | null): string | null => {
  if (!filename) return null;
  return getImageUrl(filename.startsWith('/') ? filename : `/uploads/${filename}`);
};

const InfoPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<Partial<GeneralInfo>>({
    logo: null,
    phone: '',
    emailcontact: '',
    emailadhesion: '',
    emailtraining: '',
    address: '',
    rib: '',
  });

  useEffect(() => {
    loadInfo();
  }, []);

  const loadInfo = async () => {
    try {
      setLoading(true);
      const data = await adminService.getGeneralInfo();

      setFormData({
        logo: data.logo,
        phone: data.phone || '',
        emailcontact: data.emailcontact || '',
        emailadhesion: data.emailadhesion || '',
        emailtraining: data.emailtraining || '',
        address: data.address || '',
        rib: data.rib || '',
      });

      if (data.logo) {
        setLogoPreview(constructLogoUrl(data.logo));
      } else {
        setLogoPreview(null);
      }
    } catch (error: any) {
      console.error('Error loading info:', error);
      toast.error('Erreur lors du chargement des informations');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner un fichier image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      const submitData: any = { ...formData };

      if (logoFile) {
        const uploadResult = await logoUploadService.uploadLogo(logoFile);
        submitData.logo = uploadResult.filename;
      } else if (!logoPreview) {
        submitData.logo = null;
      } else {
        // Keep existing logo filename if preserved
        submitData.logo = formData.logo;
      }

      await adminService.updateGeneralInfo(submitData);
      toast.success('Informations mises à jour avec succès');
      loadInfo();
    } catch (error: any) {
      console.error('Error saving info:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6 max-w-4xl mx-auto"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary" />
            Informations Générales
          </h1>
          <p className="text-gray-500 mt-2">Gérer les informations principales de l'organisation</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-8 space-y-8">

          {/* Logo Section */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Logo de l'organisation</label>
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {logoPreview ? (
                  <div className="relative group">
                    <div className="w-40 h-40 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-40 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                    <Building2 className="w-12 h-12 mb-2" />
                    <span className="text-xs">Aucun logo</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
                  <Upload className="w-4 h-4 mr-2" />
                  Choisir un fichier
                  <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                </label>
                <p className="mt-2 text-sm text-gray-500">
                  PNG, JPG ou GIF (max. 5MB)
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Phone */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                Téléphone
              </label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
                placeholder="+213..."
              />
            </div>

            {/* Email Contact */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email de contact
              </label>
              <input
                type="email"
                value={formData.emailcontact || ''}
                onChange={(e) => setFormData({ ...formData, emailcontact: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
                placeholder="contact@sapi-dz.org"
              />
            </div>

            {/* Email Adhesion */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email d'adhésion
              </label>
              <input
                type="email"
                value={formData.emailadhesion || ''}
                onChange={(e) => setFormData({ ...formData, emailadhesion: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
                placeholder="adhesion@sapi-dz.org"
              />
            </div>

            {/* Email Training */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email de formation
              </label>
              <input
                type="email"
                value={formData.emailtraining || ''}
                onChange={(e) => setFormData({ ...formData, emailtraining: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
                placeholder="formation@sapi-dz.org"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              Adresse
            </label>
            <textarea
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
              placeholder="Adresse complète du siège..."
            />
          </div>

          {/* RIB */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gray-400" />
              RIB Bancaire
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.rib || ''}
                onChange={(e) => setFormData({ ...formData, rib: e.target.value })}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors font-mono"
                placeholder="000..."
              />
              <CreditCard className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
            <p className="text-xs text-gray-500">Numéro de Relevé d'Identité Bancaire pour les paiements</p>
          </div>

          <div className="pt-6 border-t border-gray-200 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default InfoPage;
